import {sessionIdlingReset, sessionIdlingWarn, userLogout} from 'actions/session-actions';
import {clearMessaging} from 'components/messaging/messaging-context';
import ErrorActionTypes from 'constants/error-action-types';
import ErrorCodes from 'constants/error-codes';
import * as SessionConstants from 'constants/session-constants';
import * as Immutable from 'immutable';
import globalMessages from 'intl/global-messages';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import partial from 'lodash/partial';
import moment from 'moment-timezone';
import {createReducer, store} from 'redux-store';
import {disconnect} from 'socket-io';
import MeteorCookies from 'utils/meteor-cookies';
import UIErrorCodeUtils from '../utils/ui-error-code-utils';

import type {Action} from '@reduxjs/toolkit';
import type {AxiosError} from 'axios';
import type {ErrorCode} from 'constants/error-codes';
import type {UserGroupType as UserGroupTypeRef} from 'constants/LU_UserGroupTypes';
import type {List, Map, MapOf, RecordOf} from 'immutable';
import type {ActionsMap} from 'redux-store';

let sessionIdlingTimer: number | null;
let sessionIdlingWarningTimer: number | null;
let updateIdlingTimerFn: () => {type: string};

const cookies = new MeteorCookies({path: '/'});

const updateUserProfile = (data, userState) => {
    const scopedFields = ['firstName', 'middleName', 'lastName', 'email', 'userName'];

    data = data.filter((value, key) => {
        return scopedFields.indexOf(key) !== -1;
    });

    return userState.mergeDeep(data);
};

const getParameterByName = (name) => {
    if (__UNIT_TEST__) {
        return '';
    }
    name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)'),
        results = regex.exec(location.href);
    return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

const clearSessionData = (data = {}) => {
    clearMessaging();
    MeteorCookies.wipeDocumentCookies();
    disconnect();

    removeIdlingListeners();
    removeIdlingTimer();

    return {
        client: new ClientRecord({
            clientId: null,
            institutionName: null,
        }),
        clusterId: null,
        ...getBaseState(),
        ...data,
    };
};

type ClientProps = {
    institutionName: string;
    clientId: string;
    settings: object;
};

export type ClientRecord = RecordOf<ClientProps>;

export const ClientRecord = Immutable.Record<ClientProps>({
    institutionName: null,
    clientId: null,
    settings: {},
});

export type UserGroupType = MapOf<{ref: UserGroupTypeRef}>;

export type UserGroup = MapOf<{
    clientId: string;
    userGroupType: UserGroupType;
}>;

export type User = MapOf<{
    active: boolean;
    avatarSignedUrl: string;
    cluster?: MapOf<{
        clusterId: string;
        fqdn: string;
    }>;
    email: string;
    firstName: string;
    lastName: string;
    locked: boolean;
    middleName: string | null;
    number: unknown | null;
    organizations: List<unknown>;
    passwordChangeRequired: boolean;
    passwordUpdatedTs: string;
    passwordValid: boolean;
    termsAccepted: string;
    userGroups: List<UserGroup>;
    userId: string;
    userName: string;
}>;

type UnauthorizedAction = MapOf<{
    apiAction?: Action;
    response: MapOf<{
        data: MapOf<ErrorCode>;
    }>;
}>;

export type SessionStore = {
    // Base state
    allowPasswordRequest: boolean;
    allowCredentialsReset: boolean;
    user: User;
    linkId: string;
    acl: MapOf<{
        user: List<string>;
        client: List<string>;
    }>;
    unAuthorizedAction: null | UnauthorizedAction;
    roles: List<unknown>;
    passwordErrorMessage: MapOf<{
        id: string;
        defaultMessage: string;
    }> | null;
    status: Map<string, unknown>;
    lookups: Map<string, unknown>;
    getLookupRefById: () => unknown;
    getLookupIdByRef: (tableName: string, ref: string) => string;
    // Initial state
    loginError: MapOf<{errorCode: string; errorName: string}> | null;
    client: ClientRecord;
    settings: MapOf<{
        timezone: string;
        timezoneRules: Map<unknown, unknown>;
        hourTwelve: boolean;
        locale: string;
        passwordExpirationMinutes: number;
        sessionTimeoutSeconds: number;
        timeoutWarningSeconds: number;
        loginIncorrectAttempts: number;
        ssoEnabled?: boolean;
        useImperialMeasurements?: boolean;
        globalRecordingDeletionDays?: number;
        locationBasedRecordingEnabled?: boolean;
        allowScenarioRecordingDeletionDaysOverride?: boolean;
        uniqueDomain?: boolean;
        laerdalOrganizationId?: string;
    }>;
    clusterId: string;
    actionError: MapOf<{
        errorName: string;
        errorCode: string;
        traceId: string;
        time: Date;
    }>;
    userNameExists: boolean;
    emailExists: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userPreferences: MapOf<any>;
};

const getBaseState = (): Pick<
    SessionStore,
    | 'allowPasswordRequest'
    | 'allowCredentialsReset'
    | 'user'
    | 'linkId'
    | 'acl'
    | 'unAuthorizedAction'
    | 'roles'
    | 'passwordErrorMessage'
    | 'status'
    | 'lookups'
    | 'getLookupRefById'
    | 'getLookupIdByRef'
> => {
    return {
        allowPasswordRequest: false,
        allowCredentialsReset: false,
        // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type User
        user: Immutable.Map(),
        linkId: getParameterByName('link'),
        acl: Immutable.Map({user: Immutable.List<string>(), client: Immutable.List<string>()}),
        unAuthorizedAction: null,
        roles: Immutable.List(),
        passwordErrorMessage: null,
        status: Immutable.Map(),
        lookups: null,
        getLookupRefById: noop,
        // @ts-expect-error TS2322: Type (...args: any[]) => void is not assignable to type (tableName: string, ref: string) => string
        getLookupIdByRef: noop,
    };
};

export const defaultSettings = Immutable.Map({
    timezone: 'America/New_York',
    timezoneRules: Immutable.Map(),
    hourTwelve: true,
    locale: 'en-US',
    passwordExpirationMinutes: 0,
    sessionTimeoutSeconds: 0,
    timeoutWarningSeconds: 10,
    loginIncorrectAttempts: 0,
});

export const getInitialState = (): SessionStore => {
    const partialState: Pick<
        SessionStore,
        | 'linkId'
        | 'loginError'
        | 'client'
        | 'settings'
        | 'clusterId'
        | 'actionError'
        | 'userNameExists'
        | 'emailExists'
        | 'userPreferences'
        | 'lookups'
        | 'getLookupRefById'
        | 'getLookupIdByRef'
    > = {
        linkId: getParameterByName('link'),
        loginError: null,
        client: new ClientRecord({
            institutionName: cookies.get('institutionName', null),
            clientId: cookies.get('clientId', null),
        }),
        settings: defaultSettings,
        clusterId: cookies.get('clusterId', null),
        actionError: null,
        userNameExists: false,
        emailExists: false,
        userPreferences: Immutable.Map({}),
        lookups: null,
        getLookupRefById: noop,
        // @ts-expect-error TS2322: Type (...args: any[]) => void is not assignable to type (tableName: string, ref: string) => string
        getLookupIdByRef: noop,
    };

    return merge(partialState, getBaseState());
};

export const actionsMap: ActionsMap<SessionStore> = {
    // Be careful when altering this code. Our intl middleware depends on the
    // locale settings defined here, which it uses to load the correct
    // react-intl locale data and our localization messages for the given
    // locale.
    //
    // The timezoneRules and timezone are used by moment-timezone to format
    // dates for the appropriate timezone. Setting those settings on the moment
    // object is persistent for other instances of moment imported elsewhere.
    // This happens as a side effect of loading updated settings into this
    // store.
    //
    // This logic is replicated for SET_USER_SESSION_DATA which also sets the
    // settings.
    [SessionConstants.SETTINGS_SUCCESS]: (state, action) => {
        const timezone = action.data.get('timezone');
        const timezoneRules = action.data.get('timezoneRules').toJS();

        moment.tz.load(timezoneRules);
        moment.tz.setDefault(timezone);

        const search = new URLSearchParams(window.location.search);
        if (search.has('locale')) {
            action.data = action.data.set('locale', search.get('locale'));
        }

        return {
            settings: action.data, // do not merge, this should be the full data
        };
    },

    [SessionConstants.LOOKUPS_SUCCESS]: (state, action) => {
        const lookups = action.data;
        const getLookupRefById = (tableName, id) => {
            const idKey = `${tableName}Id`;
            return lookups
                .get(tableName, Immutable.Map())
                .find((i) => i.get(idKey) === id, null, Immutable.Map())
                .get('ref');
        };

        const getLookupIdByRef = (tableName: string, ref: string) => {
            const idKey = `${tableName}Id`;
            return lookups
                .get(tableName, Immutable.Map())
                .find((i) => i.get('ref') === ref, null, Immutable.Map())
                .get(idKey);
        };

        return {
            getLookupRefById,
            getLookupIdByRef,
            lookups,
        };
    },

    [SessionConstants.DISMISS_UNAUTHORIZED]: () => {
        return {
            unAuthorizedAction: null,
        };
    },

    [SessionConstants.UNAUTHORIZED_REQUEST]: (
        state,
        action: Action & {
            data: Pick<AxiosError<ErrorCode>, 'response'>;
        },
    ): Pick<SessionStore, 'unAuthorizedAction'> => {
        let errorCode = get(action, ['data', 'response', 'data']);

        if (isNil(cookies.get('token')) && isNil(errorCode)) {
            errorCode = ErrorCodes.TOKEN_INVALID;
        } else if (
            UIErrorCodeUtils.errorCodesAreEqual(errorCode, ErrorCodes.TOKEN_INVALID) ||
            UIErrorCodeUtils.errorCodesAreEqual(errorCode, ErrorCodes.TOKEN_EXPIRED)
        ) {
            cookies.remove('token');
            cookies.remove('clientId');
        }

        return {
            unAuthorizedAction: Immutable.Map({
                response: Immutable.Map({
                    data: Immutable.Map(errorCode),
                }),
            }),
        };
    },

    [SessionConstants.DISMISS_FROM_RECORDING_START]: (): Pick<SessionStore, 'unAuthorizedAction'> => {
        return {
            unAuthorizedAction: Immutable.Map({
                response: Immutable.Map({
                    data: Immutable.Map(ErrorCodes.UNAUTHORIZED_SESSION_START_USER),
                }),
            }),
        };
    },
    [ErrorActionTypes.ACTION_ERROR]: (state, action: Action & {data: SessionStore['actionError']}) => {
        if (!isNil(cookies.get('token')) && !state.user.isEmpty()) {
            return {
                actionError: action.data,
            };
        }
        return {
            actionError: null,
        };
    },
    [ErrorActionTypes.DISMISS_ACTION_ERROR]: () => {
        return {
            actionError: null,
        };
    },
    [SessionConstants.SETTINGS_UPDATE_SUCCESS]: (state, action) => {
        return {
            settings: state.settings.merge(action.data),
        };
    },
    [SessionConstants.AUTHENTICATE_USER_SUCCESS]: (state, action) => {
        const {data} = action;

        if (data.clusterId) {
            // We're logging in directly to the cluster
            cookies.set('clusterId', data.clusterId);
            cookies.set('userId', data.userId);
            cookies.set('token', data.token);
            cookies.remove('clientId');

            return {
                clusterId: data.clusterId,
                loginError: null,
            };
        } else {
            cookies
                .set('clientId', data.clientId)
                .set('institutionName', data.institutionName)
                .set('userId', data.userId)
                .set('token', data.token);

            return {
                client: new ClientRecord({
                    clientId: data.clientId,
                    institutionName: data.institutionName,
                }),
                loginError: null,
            };
        }
    },

    [SessionConstants.RESTORE_STORE]: () => {
        return {
            client: new ClientRecord({
                institutionName: cookies.get('institutionName', null),
                clientId: cookies.get('clientId', null),
            }),
            clusterId: cookies.get('clusterId', null),
            loginError: null,
        };
    },

    [SessionConstants.AUTHENTICATE_USER_FAILURE]: (state, action) => {
        return clearSessionData({
            loginError: Immutable.Map(action.data),
            lookups: state.lookups,
        });
    },

    [SessionConstants.GET_CLIENT_SSO_LOGIN_URL_FAILURE]: (state, action) => {
        return {
            loginError: Immutable.Map(action.data),
        };
    },

    [SessionConstants.USER_PROFILE_SUCCESS]: (state, action) => {
        return {
            user: action.data,
        };
    },

    [SessionConstants.USER_PERMISSIONS_SUCCESS]: (state, action) => {
        return {
            acl: action.data,
        };
    },

    // Be careful when altering this code. Our intl middleware depends on the
    // locale settings defined here, which it uses to load the correct
    // react-intl locale data and our localization messages for the given
    // locale.
    //
    // The timezoneRules and timezone are used by moment-timezone to format
    // dates for the appropriate timezone. Setting those settings on the moment
    // object is persistent for other instances of moment imported elsewhere.
    // This happens as a side effect of loading updated settings into this
    // store.
    //
    // This logic was originally implemented for SETTINGS_SUCCESS which also
    // sets the settings.
    [SessionConstants.SET_USER_SESSION_DATA]: (state, action) => {
        const {settings} = action.data;

        const timezone = settings.get('timezone');
        const timezoneRules = settings.get('timezoneRules').toJS();

        moment.tz.load(timezoneRules);
        moment.tz.setDefault(timezone);

        const search = new URLSearchParams(window.location.search);

        if (search.has('locale')) {
            action.data.settings = settings.set('locale', search.get('locale'));
        }

        return {
            acl: action.data.userPermissions,
            settings: action.data.settings,
            user: action.data.userProfile,
            userPreferences: action.data.userPreferences.get('settings'),
        };
    },

    [SessionConstants.PROFILE_CONTACT_EDIT_SUCCESS]: (state, action) => {
        let data = action.data;

        data = updateUserProfile(data, state.user);

        return {
            user: data,
        };
    },

    [SessionConstants.PROFILE_PASSWORD_EDIT_SUCCESS]: (state) => {
        let {status, user} = state;
        user = user.set('passwordValid', true);
        status = status.set('passwordExpired', false);

        return {
            user,
            status,
        };
    },
    [SessionConstants.PROFILE_PASSWORD_EDIT_FAILURE]: (state, action): Pick<SessionStore, 'passwordErrorMessage'> => {
        let messageDescriptor: {id: string; defaultMessage: string};
        if (!isNil(action.data)) {
            if (action.data.get('statusCode') === 401) {
                messageDescriptor = globalMessages.unableToAuthenticate;
            } else {
                switch (action.data.get('errorCode')) {
                    case ErrorCodes.REPEAT_PASSWORD:
                        messageDescriptor = globalMessages.oldMatch;
                        break;
                    case ErrorCodes.MISMATCH_PASSWORD:
                        messageDescriptor = globalMessages.incorrect;
                        break;
                    default:
                        messageDescriptor = globalMessages.unknownPasswordError;
                }
            }
        }
        return {
            passwordErrorMessage: Immutable.Map(messageDescriptor),
        };
    },
    [SessionConstants.DISPOSE_SESSION_STORE]: () => {
        return clearSessionData();
    },
    [SessionConstants.LOGOUT_USER_SUCCESS]: () => {
        cookies.remove('clientId');
        return clearSessionData();
    },
    [SessionConstants.LOGOUT_USER_FAILURE]: () => {
        cookies.remove('clientId');
        return clearSessionData({
            loginError: Immutable.Map(ErrorCodes.TOKEN_INVALID),
        });
    },
    [SessionConstants.DISPOSE_PASSWORD_STORE]: (): Pick<SessionStore, 'passwordErrorMessage'> => {
        return {
            passwordErrorMessage: null,
        };
    },
    [SessionConstants.DISPOSE_STORES_FOR_MULTIPLE_USERS]: () => {
        return {
            ...getInitialState(),
            client: new ClientRecord({
                institutionName: cookies.get('institutionName', null),
                clientId: cookies.get('clientId', null),
            }),
        };
    },

    [SessionConstants.PROFILE_CHECK_USERNAME_SUCCESS]: (state, action) => {
        return {
            userNameExists: action.data,
        };
    },

    [SessionConstants.PROFILE_CHECK_EMAIL_SUCCESS]: (state, action) => {
        return {
            emailExists: action.data,
        };
    },

    [SessionConstants.FETCH_USER_PREFERENCES_SUCCESS]: (state, action) => {
        return {
            userPreferences: action.data.get('settings'),
        };
    },

    [SessionConstants.UPDATE_USER_PREFERENCES_SUCCESS]: (state, action) => {
        let userPreferences = action.data.get('settings');

        if (Immutable.Map.isMap(userPreferences)) {
            userPreferences = userPreferences.toJS();
        } else {
            userPreferences = JSON.parse(userPreferences);
        }

        return {
            userPreferences: Immutable.fromJS(userPreferences),
        };
    },

    [SessionConstants.DISMISS_STATUS_WARNING]: (state, action) => {
        return {
            status: state.status.set(action.statusType, false),
        };
    },

    // todo: these don't need to be actions on the store
    // todo: replace doesn't need to be passed on the action
    [SessionConstants.SESSION_IDLING_INIT]: (state, action) => {
        const {settings} = state;
        const {replace} = action;

        // set timer for when user will be logged out
        const sessionTimeout = settings.get('sessionTimeoutSeconds') * 1000;
        const timeoutWarning = sessionTimeout - settings.get('timeoutWarningSeconds') * 1000;

        if (!isNil(sessionTimeout) && sessionTimeout > 0) {
            setIdlingListeners(replace, sessionTimeout, timeoutWarning);
            setIdlingTimer(replace, sessionTimeout, timeoutWarning);
        }

        return {};
    },

    [SessionConstants.SESSION_IDLING_DEACTIVATE]: () => {
        removeIdlingTimer();
        removeIdlingListeners();
    },

    [SessionConstants.SESSION_IDLING_RESET]: (state) => {
        const {status} = state;

        return {
            status: !isNil(status.get('timeout')) ? status.delete('timeout') : status,
        };
    },

    [SessionConstants.SESSION_IDLING_WARN]: (state) => {
        return {
            status: state.status.set('timeout', true),
        };
    },
};

export default createReducer(getInitialState(), actionsMap);

const setIdlingListeners = (replace, sessionTimeout, timeoutWarning) => {
    removeIdlingListeners();
    updateIdlingTimerFn = partial(updateIdlingTimer, replace, sessionTimeout, timeoutWarning);
    SessionConstants.SESSION_IDLING_EVENTS.forEach((value) => {
        window.addEventListener(value, updateIdlingTimerFn);
    });
};

const removeIdlingListeners = () => {
    if (!isNil(updateIdlingTimerFn)) {
        SessionConstants.SESSION_IDLING_EVENTS.forEach((value) => {
            window.removeEventListener(value, updateIdlingTimerFn);
        });
    }
    updateIdlingTimerFn = null;
};

const setIdlingTimer = (replace, sessionTimeout, timeoutWarning) => {
    // todo: don't pass replace
    removeIdlingTimer();
    sessionIdlingTimer = window.setTimeout(() => {
        sessionIdlingTimer = null;
        // TODO: MET-4363 TOKEN_EXPIRED error can occur before sessionTimeout which leads to double warnings
        const successFn = partial(replace, '/login');
        store.dispatch(
            // @ts-expect-error TS2345: Argument of type (dispatch: any) => any is not assignable to parameter of type Action
            userLogout(successFn, successFn),
        );
    }, sessionTimeout);

    if (timeoutWarning > 0) {
        sessionIdlingWarningTimer = window.setTimeout(() => {
            sessionIdlingWarningTimer = null;
            if (!isNil(sessionIdlingTimer)) {
                store.dispatch(sessionIdlingWarn());
            }
        }, timeoutWarning);
    }
};

const removeIdlingTimer = () => {
    if (!isNil(sessionIdlingTimer)) {
        window.clearTimeout(sessionIdlingTimer);
        sessionIdlingTimer = null;
    }
    if (!isNil(sessionIdlingWarningTimer)) {
        window.clearTimeout(sessionIdlingWarningTimer);
        sessionIdlingWarningTimer = null;
    }
};

/**
 * When the user performs a keyboard / touch / mouse activity, set the idling
 * timer to expire sessionTimeout seconds from now. Since activity happens in
 * bursts, we debounce.
 */
const updateIdlingTimer = debounce(
    (replace, sessionTimeout, timeoutWarning) => {
        if (!isNil(sessionIdlingTimer)) {
            setIdlingTimer(replace, sessionTimeout, timeoutWarning);
        }
        return store.dispatch(sessionIdlingReset());
    },
    1000,
    {leading: true, trailing: true},
);
